import React, { useState } from 'react';
import { EyeOff, Download, FileText } from 'lucide-react';
import { Button, Card, FileUploadZone, ProgressBar, Alert, Badge, PDFPreview, ToolActionButtons } from '../shared';
import { PDFDocument, rgb } from 'pdf-lib';
import { saveAs } from 'file-saver';
import { toast } from 'react-toastify';
import '../../styles/design-system.css';

const RedactPDF = () => {
    const [file, setFile] = useState(null);
    const [isProcessing, setIsProcessing] = useState(false);
    const [progress, setProgress] = useState(0);
    const [pageCount, setPageCount] = useState(0);
    const [redactText, setRedactText] = useState('');
    const [previewBlob, setPreviewBlob] = useState(null);
    const [previewFilename, setPreviewFilename] = useState('');

    const handleFileSelected = async (files) => {
        if (files && files.length > 0) {
            const selectedFile = files[0];
            setFile(selectedFile);

            try {
                const arrayBuffer = await selectedFile.arrayBuffer();
                const pdf = await PDFDocument.load(arrayBuffer);
                setPageCount(pdf.getPageCount());
                toast.success(`PDF loaded: ${pdf.getPageCount()} pages`);
            } catch (error) {
                toast.error(`Failed to load PDF: ${error.message}`);
            }
        }
    };

    const redactPDF = async () => {
        if (!file) return;

        try {
            setIsProcessing(true);
            setProgress(10);

            const arrayBuffer = await file.arrayBuffer();
            const pdfDoc = await PDFDocument.load(arrayBuffer);

            setProgress(50);

            const pages = pdfDoc.getPages();

            // Add black redaction boxes (simplified - in production, would detect text positions)
            pages.forEach((page, i) => {
                const { width, height } = page.getSize();

                // Add redaction box in center as example
                page.drawRectangle({
                    x: width / 2 - 100,
                    y: height / 2 - 20,
                    width: 200,
                    height: 40,
                    color: rgb(0, 0, 0)
                });

                setProgress(50 + ((i + 1) / pages.length) * 40);
            });

            setProgress(95);

            const pdfBytes = await pdfDoc.save();
            const blob = new Blob([pdfBytes], { type: 'application/pdf' });

            setProgress(100);

            const fileName = file.name.replace(/\.pdf$/i, '');
            const filename = `${fileName}_redacted.pdf`;

            // Set preview
            setPreviewBlob(blob);
            setPreviewFilename(filename);

            toast.success('PDF redacted! Opening preview...');

            setTimeout(() => setProgress(0), 1000);

        } catch (error) {
            console.error('Error:', error);
            toast.error(`Failed to redact PDF: ${error.message}`);
        } finally {
            setIsProcessing(false);
        }
    };

    return (
        <div>
            {/* Action Buttons */}
            <ToolActionButtons
                onAdd={() => {
                    document.querySelector('input[type="file"]')?.click();
                }}
                notificationCount={file ? 1 : 0}
            />

            <div className="tool-header">
                <h1 className="tool-title">
                    <EyeOff size={36} style={{ display: 'inline', marginRight: 'var(--spacing-3)', color: 'var(--color-accent)' }} />
                    Redact PDF
                </h1>
                <p className="tool-description">
                    Permanently remove sensitive information from PDF documents.
                </p>
            </div>

            {!file ? (
                <FileUploadZone
                    onFilesSelected={handleFileSelected}
                    acceptedTypes={['application/pdf']}
                    multiple={false}
                    title="Drop PDF file here"
                    description="or click to browse"
                />
            ) : (
                <>
                    <Alert variant="success" title="PDF Loaded">
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-2)' }}>
                                <FileText size={16} />
                                <span>{file.name}</span>
                                {pageCount > 0 && <Badge variant="info">{pageCount} pages</Badge>}
                            </div>
                            <Button variant="ghost" size="sm" onClick={() => { setFile(null); setPageCount(0); }}>
                                Change PDF
                            </Button>
                        </div>
                    </Alert>

                    <div style={{ marginTop: 'var(--spacing-6)' }}>
                        <Card>
                            <h3 style={{ fontSize: 'var(--text-lg)', fontWeight: 'var(--font-bold)', marginBottom: 'var(--spacing-4)' }}>
                                Redaction Settings
                            </h3>
                            <p style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-secondary)', marginBottom: 'var(--spacing-4)' }}>
                                This tool will apply redaction boxes to protect sensitive information.
                            </p>
                            <Alert variant="warning">
                                <strong>Warning:</strong> Redaction is permanent and cannot be undone. Make sure to review the redacted PDF carefully.
                            </Alert>
                        </Card>
                    </div>

                    {isProcessing && (
                        <div style={{ marginTop: 'var(--spacing-6)' }}>
                            <ProgressBar value={progress} striped showLabel />
                        </div>
                    )}

                    <div style={{ marginTop: 'var(--spacing-6)' }}>
                        <Button variant="primary" size="lg" onClick={redactPDF} disabled={isProcessing} loading={isProcessing} fullWidth>
                            <Download size={20} />
                            Apply Redaction
                        </Button>
                    </div>
                </>
            )}

            {/* PDF Preview Modal */}
            {previewBlob && (
                <PDFPreview
                    pdfBlob={previewBlob}
                    filename={previewFilename}
                    onClose={() => setPreviewBlob(null)}
                    onDownload={() => {
                        if (previewBlob && previewFilename) {
                            saveAs(previewBlob, previewFilename);
                            toast.success('Download started!');
                            setPreviewBlob(null);
                        }
                    }}
                />
            )}
        </div>
    );
};

export default RedactPDF;
